import React, { useState, useEffect, useCallback, memo } from 'react';
import axios from 'axios';
import { FileText, Save, Loader2, Upload, Image as ImageIcon, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ContentField ist AUSSERHALB der Hauptkomponente definiert - Das war der Bug!
// Jetzt wird es nicht mehr bei jedem Render neu erstellt
const ContentField = memo(({ label, fieldKey, value, onChange, type = 'input', rows = 3, description }) => {
  const handleChange = useCallback((e) => {
    onChange(fieldKey, e.target.value);
  }, [fieldKey, onChange]);

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldKey}>{label}</Label>
      {description && <p className="text-sm text-slate-500">{description}</p>}
      {type === 'textarea' ? (
        <Textarea
          id={fieldKey}
          value={value || ''}
          onChange={handleChange}
          rows={rows}
          data-testid={`cms-${fieldKey}`}
        />
      ) : (
        <Input
          id={fieldKey}
          value={value || ''}
          onChange={handleChange}
          data-testid={`cms-${fieldKey}`}
        />
      )}
    </div>
  );
});

ContentField.displayName = 'ContentField';

// Hero Image Upload Komponente
const HeroImageUpload = memo(({ currentImage, onUpload, uploading }) => {
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
  }, [onUpload]);

  return (
    <div className="space-y-4">
      <Label>Hero-Hintergrundbild</Label>
      <p className="text-sm text-slate-500">
        Das große Hintergrundbild im Hero-Bereich der Startseite
      </p>
      
      {currentImage && (
        <div className="relative w-full max-w-md rounded-lg overflow-hidden border border-slate-200">
          <img 
            src={currentImage} 
            alt="Aktuelles Hero-Bild" 
            className="w-full h-48 object-cover"
          />
          <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
            Aktuelles Bild
          </div>
        </div>
      )}
      
      <div className="flex items-center gap-4">
        <label 
          htmlFor="hero-image-upload" 
          className={`
            flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed 
            cursor-pointer transition-colors
            ${uploading 
              ? 'border-slate-300 bg-slate-50 cursor-not-allowed' 
              : 'border-slate-300 hover:border-primary hover:bg-primary/5'
            }
          `}
        >
          {uploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              <span className="text-sm text-slate-500">Wird hochgeladen...</span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-slate-500" />
              <span className="text-sm text-slate-700">
                {currentImage ? 'Neues Bild auswählen' : 'Bild auswählen'}
              </span>
            </>
          )}
        </label>
        <input
          id="hero-image-upload"
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading}
          className="hidden"
          data-testid="hero-image-upload-input"
        />
      </div>
      
      <p className="text-xs text-slate-400">
        Empfohlene Größe: 1920x1080px oder größer. JPG, PNG oder WebP.
      </p>
    </div>
  );
});

HeroImageUpload.displayName = 'HeroImageUpload';

const CmsPage = () => {
  const { isAdmin } = useAuth();
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroImage, setHeroImage] = useState(null);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/cms`);
      setContent(response.data);
      // Hero Image aus CMS laden - verwende API-Serve-Endpunkt
      if (response.data.hero_image) {
        // Prüfe ob ein Bild existiert und setze die Serve-URL
        // Timestamp für Cache-Busting hinzufügen
        const heroImageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/cms/serve-image/hero_image?t=${Date.now()}`;
        setHeroImage(heroImageUrl);
      }
    } catch (error) {
      console.error('Failed to fetch CMS content:', error);
      toast.error('Fehler beim Laden der Inhalte');
    } finally {
      setLoading(false);
    }
  };

  // useCallback verhindert unnötige Re-Renders der ContentField Komponenten
  const handleChange = useCallback((key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(async (key) => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms`, { key, value: content[key] || '' });
      toast.success('Inhalt gespeichert');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }, [content]);

  const handleSaveAll = useCallback(async () => {
    setSaving(true);
    try {
      const keys = Object.keys(content);
      for (const key of keys) {
        await axios.put(`${API}/cms`, { key, value: content[key] || '' });
      }
      toast.success('Alle Inhalte gespeichert');
    } catch (error) {
      console.error('Failed to save all:', error);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }, [content]);

  const handleHeroImageUpload = useCallback(async (file) => {
    setUploadingHeroImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'hero_image');
      
      const response = await axios.post(`${API}/cms/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Die API gibt jetzt den Serve-Endpunkt zurück
      const imageUrl = response.data.url;
      
      // Setze die vollständige URL für die Anzeige
      const fullImageUrl = imageUrl.startsWith('/api/') 
        ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`
        : imageUrl;
      
      setHeroImage(fullImageUrl);
      
      // Das Backend speichert bereits den Storage-Pfad, kein zusätzliches PUT nötig
      setContent(prev => ({ ...prev, hero_image: response.data.path }));
      
      toast.success('Hero-Bild erfolgreich hochgeladen');
    } catch (error) {
      console.error('Failed to upload hero image:', error);
      toast.error('Hochladen fehlgeschlagen');
    } finally {
      setUploadingHeroImage(false);
    }
  }, []);

  if (!isAdmin()) {
    return (
      <div className="text-center py-16">
        <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">Keine Berechtigung</h3>
        <p className="text-slate-500">Diese Seite ist nur für Administratoren zugänglich.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 heading-app">CMS</h1>
          <p className="text-slate-500">Bearbeiten Sie alle Inhalte der Website</p>
        </div>
        <Button onClick={handleSaveAll} disabled={saving} data-testid="save-all-button">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Alle speichern
        </Button>
      </div>

      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList className="grid grid-cols-4 lg:grid-cols-7 gap-2">
          <TabsTrigger value="hero" data-testid="tab-hero">Hero</TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload">Upload</TabsTrigger>
          <TabsTrigger value="success" data-testid="tab-success">Erfolg</TabsTrigger>
          <TabsTrigger value="footer" data-testid="tab-footer">Footer</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal">Rechtstexte</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">E-Mail</TabsTrigger>
          <TabsTrigger value="other" data-testid="tab-other">Sonstiges</TabsTrigger>
        </TabsList>

        <TabsContent value="hero">
          <Card>
            <CardHeader>
              <CardTitle>Hero-Bereich</CardTitle>
              <CardDescription>
                Der große Bereich oben auf der Startseite
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hero Image Upload - Neues Feature */}
              <HeroImageUpload 
                currentImage={heroImage}
                onUpload={handleHeroImageUpload}
                uploading={uploadingHeroImage}
              />
              
              <div className="border-t border-slate-200 pt-6" />
              
              <ContentField
                label="Kooperations-/Hinweistext (Pill)"
                fieldKey="hero_koop"
                value={content.hero_koop}
                onChange={handleChange}
                description="Kleiner Text im Badge über der Headline"
              />
              <ContentField
                label="Hauptüberschrift"
                fieldKey="hero_title"
                value={content.hero_title}
                onChange={handleChange}
                description="Die große Überschrift im Hero-Bereich"
              />
              <ContentField
                label="Beschreibung"
                fieldKey="hero_sub"
                value={content.hero_sub}
                onChange={handleChange}
                type="textarea"
                rows={3}
                description="Der erklärende Text unter der Hauptüberschrift"
              />
              <ContentField
                label="Button-Text"
                fieldKey="hero_button"
                value={content.hero_button}
                onChange={handleChange}
                description="Text auf dem Hauptbutton"
              />
              <ContentField
                label="Info-Box Titel"
                fieldKey="hero_box_title"
                value={content.hero_box_title}
                onChange={handleChange}
                description="Fetter Text in der Info-Box neben dem Button"
              />
              <ContentField
                label="Info-Box Text"
                fieldKey="hero_box_text"
                value={content.hero_box_text}
                onChange={handleChange}
                description="Grauer Text in der Info-Box"
              />
              <ContentField
                label="Pickup-/Standort-Hinweis"
                fieldKey="hero_pickup"
                value={content.hero_pickup}
                onChange={handleChange}
                description="Text mit Standort-Icon unter dem Button"
              />
              <ContentField
                label="Zusätzlicher Hinweis"
                fieldKey="hero_hint"
                value={content.hero_hint}
                onChange={handleChange}
                description="Kursiver Hinweistext ganz unten"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="upload">
          <Card>
            <CardHeader>
              <CardTitle>Upload-Bereich</CardTitle>
              <CardDescription>
                Texte im Upload-Formular
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ContentField
                label="Rechte-Checkbox Text"
                fieldKey="rights_checkbox_text"
                value={content.rights_checkbox_text}
                onChange={handleChange}
                type="textarea"
                rows={4}
                description="Der rechtliche Hinweistext bei der Checkbox"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="success">
          <Card>
            <CardHeader>
              <CardTitle>Erfolgsseite</CardTitle>
              <CardDescription>
                Texte nach erfolgreichem Upload
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ContentField
                label="Erfolgs-Titel"
                fieldKey="upload_success_title"
                value={content.upload_success_title}
                onChange={handleChange}
                description="Überschrift nach erfolgreichem Upload"
              />
              <ContentField
                label="Erfolgs-Text"
                fieldKey="upload_success_text"
                value={content.upload_success_text}
                onChange={handleChange}
                type="textarea"
                rows={3}
                description="Beschreibungstext nach erfolgreichem Upload"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="footer">
          <Card>
            <CardHeader>
              <CardTitle>Footer</CardTitle>
              <CardDescription>
                Texte und Links im Seitenfuß
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ContentField
                label="Footer-Text"
                fieldKey="footer_text"
                value={content.footer_text}
                onChange={handleChange}
                description="Copyright-Text im Footer"
              />
              <ContentField
                label="Datenschutz-Link"
                fieldKey="footer_privacy_link"
                value={content.footer_privacy_link}
                onChange={handleChange}
                description="URL zur Datenschutzerklärung"
              />
              <ContentField
                label="Impressum-Link"
                fieldKey="footer_imprint_link"
                value={content.footer_imprint_link}
                onChange={handleChange}
                description="URL zum Impressum"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="legal">
          <Card>
            <CardHeader>
              <CardTitle>Rechtstexte</CardTitle>
              <CardDescription>
                Impressum, Datenschutzerklärung und AGB
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ContentField
                label="Impressum"
                fieldKey="legal_imprint"
                value={content.legal_imprint}
                onChange={handleChange}
                type="textarea"
                rows={12}
                description="Vollständiges Impressum gemäß §5 TMG"
              />
              <ContentField
                label="Datenschutzerklärung"
                fieldKey="legal_privacy"
                value={content.legal_privacy}
                onChange={handleChange}
                type="textarea"
                rows={12}
                description="Datenschutzerklärung gemäß DSGVO"
              />
              <ContentField
                label="AGB"
                fieldKey="legal_terms"
                value={content.legal_terms}
                onChange={handleChange}
                type="textarea"
                rows={12}
                description="Allgemeine Geschäftsbedingungen"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle>E-Mail Benachrichtigungen</CardTitle>
              <CardDescription>
                Konfigurieren Sie die E-Mails, die bei neuen Uploads versendet werden
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ContentField
                label="E-Mail Betreff"
                fieldKey="email_subject"
                value={content.email_subject}
                onChange={handleChange}
                description="Betreffzeile der Benachrichtigungs-E-Mail"
              />
              <ContentField
                label="E-Mail Text"
                fieldKey="email_body"
                value={content.email_body}
                onChange={handleChange}
                type="textarea"
                rows={8}
                description="Inhalt der E-Mail. Platzhalter: {sender_name}, {sender_email}, {image_count}, {description}"
              />
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">Verfügbare Platzhalter:</p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li><code className="bg-blue-100 px-1 rounded">{'{sender_name}'}</code> - Name des Absenders</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{sender_email}'}</code> - E-Mail des Absenders</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{image_count}'}</code> - Anzahl der hochgeladenen Bilder</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{description}'}</code> - Beschreibung des Uploads</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="other">
          <Card>
            <CardHeader>
              <CardTitle>Sonstige Inhalte</CardTitle>
              <CardDescription>
                Weitere bearbeitbare Texte
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-slate-500">
                Weitere Inhalte können hier hinzugefügt werden, sobald sie benötigt werden.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Hinweis:</strong> Alle Texte werden als einfacher Text gespeichert. 
          HTML-Formatierung wird nicht unterstützt. 
          Änderungen werden erst nach dem Speichern auf der Website sichtbar.
        </p>
      </div>
    </div>
  );
};

export default CmsPage;
